/**
 * Billiards Physics Engine
 *
 * 2D physics engine for circle collisions on a billiards table.
 * Handles: position updates, friction (rolling + sliding), cushion bouncing,
 * circle-circle elastic collisions, 6 pocket detection, spin effects,
 * and stop detection.
 *
 * Runs at sub-stepped 60fps for deterministic results.
 */

import {
  BilliardsBall,
  BilliardsPhysicsConfig,
  DEFAULT_BILLIARDS_PHYSICS_CONFIG,
  Vector2D,
  SpinType,
} from './types';

export class BilliardsPhysics {
  private balls: BilliardsBall[] = [];
  private config: BilliardsPhysicsConfig;
  private pocketedThisFrame: BilliardsBall[] = [];
  private onPocketed?: (ball: BilliardsBall) => void;
  private railContactOccurred: boolean = false;
  private firstBallHitType?: string;
  private firstBallHitId?: string;

  constructor(config: BilliardsPhysicsConfig = DEFAULT_BILLIARDS_PHYSICS_CONFIG) {
    this.config = config;
  }

  // =========================================================================
  // Ball Management
  // =========================================================================

  addBall(ball: BilliardsBall): void {
    this.balls.push(ball);
  }

  removeBall(id: string): void {
    this.balls = this.balls.filter((b) => b.id !== id);
  }

  getBall(id: string): BilliardsBall | undefined {
    return this.balls.find((b) => b.id === id);
  }

  getBalls(): BilliardsBall[] {
    return this.balls;
  }

  getActiveBalls(): BilliardsBall[] {
    return this.balls.filter((b) => !b.pocketed);
  }

  setOnPocketed(callback: (ball: BilliardsBall) => void): void {
    this.onPocketed = callback;
  }

  clearBalls(): void {
    this.balls = [];
  }

  resetTrackingState(): void {
    this.railContactOccurred = false;
    this.firstBallHitType = undefined;
    this.firstBallHitId = undefined;
  }

  getRailContactOccurred(): boolean {
    return this.railContactOccurred;
  }

  getFirstBallHitType(): string | undefined {
    return this.firstBallHitType;
  }

  getFirstBallHitId(): string | undefined {
    return this.firstBallHitId;
  }

  // =========================================================================
  // Physics Step
  // =========================================================================

  /**
   * Advance physics simulation by dt seconds.
   * Uses fixed sub-stepping for stability with high velocities.
   */
  step(dt: number): BilliardsBall[] {
    this.pocketedThisFrame = [];
    const activeBalls = this.getActiveBalls();

    if (activeBalls.length === 0) return this.pocketedThisFrame;

    // Fixed sub-step at 1/120s for smooth, stable simulation
    const fixedSubStep = 1 / 120;
    let remaining = dt;

    while (remaining > 0) {
      const subDt = Math.min(remaining, fixedSubStep);
      this.substep(subDt, activeBalls);
      remaining -= subDt;
    }

    return this.pocketedThisFrame;
  }

  private substep(dt: number, activeBalls: BilliardsBall[]): void {
    // 1. Apply friction
    for (const ball of activeBalls) {
      if (!ball.pocketed) {
        this.applyFriction(ball, dt);
      }
    }

    // 2. Update positions
    for (const ball of activeBalls) {
      if (!ball.pocketed) {
        ball.x += ball.vx * dt;
        ball.y += ball.vy * dt;
      }
    }

    // 3. Cushion collisions
    for (const ball of activeBalls) {
      if (!ball.pocketed) {
        this.resolveCushionCollision(ball);
      }
    }

    // 4. Ball-ball collisions (multiple passes for stability)
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < activeBalls.length; i++) {
        for (let j = i + 1; j < activeBalls.length; j++) {
          const a = activeBalls[i];
          const b = activeBalls[j];
          if (!a.pocketed && !b.pocketed) {
            this.resolveBallCollision(a, b);
          }
        }
      }
    }

    // 5. Pocket detection
    for (const ball of activeBalls) {
      if (!ball.pocketed) {
        this.checkPockets(ball);
      }
    }

    // 6. Stop slow balls
    for (const ball of activeBalls) {
      if (!ball.pocketed) {
        const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
        if (speed < this.config.stopThreshold) {
          ball.vx = 0;
          ball.vy = 0;
        }
      }
    }
  }

  // =========================================================================
  // Friction
  // =========================================================================

  /**
   * Apply frame-rate-independent friction.
   * Combines rolling friction (dominant at lower speeds) and sliding friction.
   */
  applyFriction(ball: BilliardsBall, dt: number): void {
    const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
    if (speed === 0) return;

    // Use rolling friction as the main damping factor
    const factor = Math.pow(this.config.rollingFriction, dt * 60);
    ball.vx *= factor;
    ball.vy *= factor;

    // Additional sliding friction for higher speeds
    if (speed > 100) {
      const slideFactor = Math.pow(this.config.friction, dt * 60);
      ball.vx *= slideFactor;
      ball.vy *= slideFactor;
    }
  }

  // =========================================================================
  // Cushion Collision
  // =========================================================================

  resolveCushionCollision(ball: BilliardsBall): void {
    const minX = this.config.cushionWidth + ball.radius;
    const maxX = this.config.tableWidth - this.config.cushionWidth - ball.radius;
    const minY = this.config.cushionWidth + ball.radius;
    const maxY = this.config.tableHeight - this.config.cushionWidth - ball.radius;

    // Skip bounce if near a pocket
    if (this.isNearPocket(ball)) {
      return;
    }

    let hitCushion = false;

    if (ball.x < minX) {
      ball.x = minX;
      ball.vx = Math.abs(ball.vx) * this.config.cushionRestitution;
      hitCushion = true;
    } else if (ball.x > maxX) {
      ball.x = maxX;
      ball.vx = -Math.abs(ball.vx) * this.config.cushionRestitution;
      hitCushion = true;
    }

    if (ball.y < minY) {
      ball.y = minY;
      ball.vy = Math.abs(ball.vy) * this.config.cushionRestitution;
      hitCushion = true;
    } else if (ball.y > maxY) {
      ball.y = maxY;
      ball.vy = -Math.abs(ball.vy) * this.config.cushionRestitution;
      hitCushion = true;
    }

    if (hitCushion) {
      this.railContactOccurred = true;
    }
  }

  private isNearPocket(ball: BilliardsBall): boolean {
    for (const pocket of this.config.pocketPositions) {
      const dx = ball.x - pocket.x;
      const dy = ball.y - pocket.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < this.config.pocketRadius + ball.radius) {
        return true;
      }
    }
    return false;
  }

  // =========================================================================
  // Ball-Ball Elastic Collision
  // =========================================================================

  resolveBallCollision(a: BilliardsBall, b: BilliardsBall): void {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const minDist = a.radius + b.radius;

    if (dist >= minDist || dist === 0) return;

    // Track first ball hit by cue ball
    if (a.type === 'cue' && !this.firstBallHitId) {
      this.firstBallHitId = b.id;
      this.firstBallHitType = b.type;
    } else if (b.type === 'cue' && !this.firstBallHitId) {
      this.firstBallHitId = a.id;
      this.firstBallHitType = a.type;
    }

    // Normal vector
    const nx = dx / dist;
    const ny = dy / dist;

    // Relative velocity along normal
    const dvn = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;

    // Only resolve if approaching
    if (dvn <= 0) return;

    // Impulse scalar (elastic collision with restitution)
    const e = this.config.ballRestitution;
    const invMassSum = 1 / a.mass + 1 / b.mass;
    const j = (1 + e) * dvn / invMassSum;

    // Apply impulse
    a.vx -= (j / a.mass) * nx;
    a.vy -= (j / a.mass) * ny;
    b.vx += (j / b.mass) * nx;
    b.vy += (j / b.mass) * ny;

    // Separate overlapping balls
    const overlap = minDist - dist;
    const totalMass = a.mass + b.mass;
    const separationFactor = 1.01;
    a.x -= (overlap * separationFactor * (b.mass / totalMass)) * nx;
    a.y -= (overlap * separationFactor * (b.mass / totalMass)) * ny;
    b.x += (overlap * separationFactor * (a.mass / totalMass)) * nx;
    b.y += (overlap * separationFactor * (a.mass / totalMass)) * ny;
  }

  // =========================================================================
  // Pocket Detection
  // =========================================================================

  checkPockets(ball: BilliardsBall): boolean {
    for (const pocket of this.config.pocketPositions) {
      const dx = ball.x - pocket.x;
      const dy = ball.y - pocket.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < this.config.pocketRadius) {
        ball.pocketed = true;
        ball.vx = 0;
        ball.vy = 0;
        this.pocketedThisFrame.push(ball);

        if (this.onPocketed) {
          this.onPocketed(ball);
        }
        return true;
      }
    }
    return false;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  areAllStopped(): boolean {
    const activeBalls = this.getActiveBalls();
    for (const ball of activeBalls) {
      const speed = Math.sqrt(ball.vx * ball.vx + ball.vy * ball.vy);
      if (speed > this.config.stopThreshold) {
        return false;
      }
    }
    return true;
  }

  /**
   * Apply a shot to the cue ball.
   * @param angle - Direction angle in radians
   * @param power - Power from 0 to 1
   * @param spin - Spin type affecting post-collision behavior
   */
  applyShot(angle: number, power: number, spin: SpinType = 'none'): void {
    const cueBall = this.balls.find((b) => b.type === 'cue');
    if (!cueBall || cueBall.pocketed) return;

    const clampedPower = Math.max(0, Math.min(1, power));
    const speed = clampedPower * this.config.maxSpeed;

    cueBall.vx = Math.cos(angle) * speed;
    cueBall.vy = Math.sin(angle) * speed;

    // Apply spin effects to initial velocity
    const spinFactor = 0.15;
    switch (spin) {
      case 'top':
        // Top spin: slightly more speed in shot direction
        cueBall.vx *= (1 + spinFactor * 0.5);
        cueBall.vy *= (1 + spinFactor * 0.5);
        break;
      case 'back':
        // Back spin: slightly less initial speed (energy goes to spin)
        cueBall.vx *= (1 - spinFactor * 0.3);
        cueBall.vy *= (1 - spinFactor * 0.3);
        break;
      case 'left':
        // Left english: deflect trajectory slightly left
        {
          const cos = Math.cos(-spinFactor * 0.3);
          const sin = Math.sin(-spinFactor * 0.3);
          const newVx = cueBall.vx * cos - cueBall.vy * sin;
          const newVy = cueBall.vx * sin + cueBall.vy * cos;
          cueBall.vx = newVx;
          cueBall.vy = newVy;
        }
        break;
      case 'right':
        // Right english: deflect trajectory slightly right
        {
          const cos = Math.cos(spinFactor * 0.3);
          const sin = Math.sin(spinFactor * 0.3);
          const newVx = cueBall.vx * cos - cueBall.vy * sin;
          const newVy = cueBall.vx * sin + cueBall.vy * cos;
          cueBall.vx = newVx;
          cueBall.vy = newVy;
        }
        break;
    }

    // Reset tracking state for this shot
    this.resetTrackingState();
  }

  /**
   * Place the cue ball at a specific position (ball-in-hand).
   */
  placeCueBall(x: number, y: number): void {
    const cueBall = this.balls.find((b) => b.type === 'cue');
    if (!cueBall) return;

    cueBall.x = x;
    cueBall.y = y;
    cueBall.vx = 0;
    cueBall.vy = 0;
    cueBall.pocketed = false;
  }

  // =========================================================================
  // Utility
  // =========================================================================

  static distance(a: Vector2D, b: Vector2D): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  static normalize(v: Vector2D): Vector2D {
    const len = Math.sqrt(v.x * v.x + v.y * v.y);
    if (len === 0) return { x: 0, y: 0 };
    return { x: v.x / len, y: v.y / len };
  }

  static angleBetween(from: Vector2D, to: Vector2D): number {
    return Math.atan2(to.y - from.y, to.x - from.x);
  }

  updateConfig(partial: Partial<BilliardsPhysicsConfig>): void {
    this.config = { ...this.config, ...partial };
  }

  getConfig(): BilliardsPhysicsConfig {
    return { ...this.config };
  }
}
